import React, { useState, Fragment, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Media,
  Input,
  Form,
  Button,
} from "reactstrap";
import Avatar from "../common/Avatar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Flex from "../common/Flex";
import ButtonImgPostCreate from "./ButtonImgPostCreate";
import FalconCardHeader from "../common/FalconCardHeader";
import postImage from "../../assets/img/illustrations/image.svg";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Slider from "react-slick";
import { createPost } from "../../helpers/api/post";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import classNames from "classnames";
import WizardError from "../auth/register/WizardError";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { Toast } from "../UI/toast/Toast";
import { checkUserToken } from "../../helpers/api/auth";
import { PreloadState } from "../UI/preloading/PreloadState";


const PostCreateForm = ({ mutate }) => {
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { token, role } = useSelector((state) => state.auth.user);
  const { register, handleSubmit, setError, clearErrors, errors, setValue } =
    useForm({ mode: "onChange", reValidateMode: "onChange" });
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("");
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const inputFile = useRef();
  const [profile, setProfile] = useState({
    data: null,
    error: null,
    loading: true,
  });
  const [nav1, setNav1] = useState(null);

  let slider1;

  useEffect(() => {
    setNav1(slider1);
  }, [slider1]);

  register("title");
  register("status");
  register("images");

  useEffect(() => {
    setImages([]);
    setImagePreviews([]);

    if (token) {
      const getProfile = async () => {
        try {
          const { data } = await checkUserToken(token);
          setProfile((prev) => {
            return { ...prev, data: data, isLoading: false };
          });
        } catch (e) {
          setProfile((prev) => {
            return { ...prev, error: e, isLoading: false };
          });
        }
      };
      getProfile();
    }
    return () => {
      setProfile({ data: null, loading: true, error: null });
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setValue("images", images);
    // eslint-disable-next-line
  }, [images]);

  const handleImageAdd = (event) => {
    const selectedFiles = Array.from(event.target.files);

    if (images.length + selectedFiles.length > 5) {
      setError("images", {
        message: "El límite de imágenes por publicación es de 5.",
      });
      return;
    }

    clearErrors("images");

    const newImages = [...images, ...selectedFiles];
    const newPreviews = [
      ...imagePreviews,
      ...selectedFiles.map((file) => URL.createObjectURL(file)),
    ];

    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  useEffect(() => {
    if (status) {
      if (status.length < 10) {
        setError("status", {
          message: "La descripción debe tener al menos 10 carácteres.",
        });
      } else {
        clearErrors("status");
      }
    }
    if (status === "") {
      setError("status", { message: "La descripción es requerida." });
    }
    // eslint-disable-next-line
  }, [status]);

  useEffect(() => {
    if (title) {
      if (title.length < 10) {
        setError("title", {
          message: "El titulo debe tener al menos 10 carácteres.",
        });
      } else {
        clearErrors("title");
      }
    }
    if (title === "") {
      setError("title", { message: "El título es requerido." });
    }
    // eslint-disable-next-line
  }, [title]);

  const save = async (values) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setSubmitting(true);

    const { title, status, images } = values;

    let form = new FormData();
    form.append("title", title);
    form.append("description", status);
    role === "manager" && form.append("condominium", selectedCondominium);
    if (images && images.length > 0) {
      images.forEach((file, i) => form.append(`files[${i}]`, file));
    }

    createPost(form)
      .then(({ code }) => {
        if (code === SUCCESS_RESPONSE.CREATED) {
          Toast.fire({
            type: "success",
            title: `Publicación realizada exitosamente.`,
          });
          setStatus("");
          setTitle("");
          setImages([]);
          setImagePreviews([]);
          mutate(new Date());
        }
      })
      .catch((error) => {
        handleErrorMessage(error);
      })
      .finally(() => {
        isSubmittingRef.current = false;
        setSubmitting(false);
      });
  };

  return (
    <Card className="mb-3">
      <FalconCardHeader
        title={
          <Fragment>
            <Avatar
              name={profile?.data?.firstname}
              src={profile?.data?.image ? profile?.data?.image : undefined}
              size="m"
            />
            <Media body className="ml-2">
              <h5 className="mb-0 fs-0">Realizar publicación</h5>
            </Media>
          </Fragment>
        }
        titleTag={Media}
        titleClass="align-items-center"
      />
      <CardBody className="p-0">
        <PreloadState
          loading={submitting}
          message="Publicando por favor espere..."
          overlay={true}
        >
          <Form onSubmit={handleSubmit(save)}>
            <Input
              type="text"
              className="border-top-0 border-left-0 border-right-0 rounded-0 border-bottom font-weight-semi-bold"
              value={title}
              onChange={({ target }) => {
                setTitle(target.value);
                setValue("title", target.value);
              }}
              style={{ boxShadow: "none" }}
              placeholder="Escribe el título de la publicación."
            />
            {errors.title &&
              errors.title.message !== "El título es requerido." && (
                <WizardError error={errors.title.message} className="ml-2 " />
              )}
            <Input
              className="border-0 rounded-0 resize-none"
              style={{ boxShadow: "none" }}
              placeholder="Escribe lo que quieres informar"
              type="textarea"
              rows="4"
              spellCheck="false"
              value={status}
              onChange={({ target }) => {
                setStatus(target.value);
                setValue("status", target.value);
              }}
            />
            {errors.status &&
              errors.status.message !== "La descripción es requerida." && (
                <WizardError error={errors.status.message} className="ml-2 " />
              )}
            {imagePreviews.length > 0 && (
              <Slider
                asNavFor={nav1}
                ref={(slider) => (slider1 = slider)}
                slidesToShow={
                  imagePreviews.length > 4 ? 4 : imagePreviews.length
                }
                swipeToSlide={true}
                className="slick-slider-arrow-inner mt-1 w-auto"
              >
                {imagePreviews.map((file, i) => (
                  <div
                    className="cursor-pointer pr-1 outline-none position-relative w-auto"
                    key={i}
                  >
                    <button
                      className="close float-none position-absolute bg-white rounded"
                      onClick={() => {
                        const urlToRevoke = imagePreviews[i];
                        if (urlToRevoke) {
                          URL.revokeObjectURL(urlToRevoke);
                        }
                        setImagePreviews(
                          imagePreviews.filter((val, index) => index !== i)
                        );
                        setImages(images.filter((val, index) => index !== i));
                      }}
                      style={{ right: "15px", top: "15px", padding: "2px 6px" }}
                      type="button"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                    <img
                      className="img-fluid w-sm-100 h-sm-100 rounded text-center"
                      style={{ aspectRatio: "1/1", objectFit: "contain" }}
                      src={file}
                      alt="Imagen del post"
                    />
                  </div>
                ))}
              </Slider>
            )}
            <Flex align="center" className="border-bottom px-3 mt-1 pb-1">
              <label className="text-nowrap mb-0 mr-2" htmlFor="hash-tags">
                <input
                  ref={inputFile}
                  type="file"
                  onChange={handleImageAdd}
                  accept="image/png, image/jpeg"
                  style={{ display: "none" }}
                />
                <ButtonImgPostCreate
                  imgSrc={postImage}
                  disabled={submitting}
                  onClick={() => {
                    if (!submitting) {
                      inputFile.current.click();
                    }
                  }}
                >
                  <FontAwesomeIcon icon="plus" className="mr-1 fs--2" />
                  <span className="d-none d-md-inline-block">
                    Agregar imágen
                  </span>
                </ButtonImgPostCreate>
              </label>
              {errors.images && (
                <WizardError error={errors.images.message} className="ml-2 " />
              )}
            </Flex>
            <Row noGutters className="justify-content-between mt-3 px-3 pb-3">
              <Col xs="auto">
                <Button
                  disabled={!status || !title || submitting}
                  className={classNames({ "px-4 px-sm-5": true })}
                  type="submit"
                  color="primary"
                  size="sm"
                >
                  {submitting ? "Publicando..." : "Publicar"}
                </Button>
              </Col>
            </Row>
          </Form>
        </PreloadState>
      </CardBody>
    </Card>
  );
};

export default React.memo(PostCreateForm);
